/**
 * usePermissions Hook
 * Provides permission checking utilities based on user's role
 */

import { useCallback, useMemo } from 'react';
import { useOrganization } from '../providers/OrganizationProvider';
import { 
  roleHasPermission, 
  hasRoleLevel, 
  PERMISSIONS,
  ROLE_PERMISSIONS,
  getAssignableRoles,
  getRoleName,
  getRoleColor,
} from '../utils/permissions';

export function usePermissions() {
  const { role, organization } = useOrganization();

  /**
   * Check if user has a specific permission
   */
  const can = useCallback((permission) => {
    if (!role) return false;
    return roleHasPermission(role, permission);
  }, [role]);

  /**
   * Check if user has ALL of the specified permissions
   */
  const canAll = useCallback((permissions) => {
    if (!role || !permissions?.length) return false;
    return permissions.every(p => roleHasPermission(role, p));
  }, [role]);

  /**
   * Check if user has ANY of the specified permissions
   */
  const canAny = useCallback((permissions) => {
    if (!role || !permissions?.length) return false;
    return permissions.some(p => roleHasPermission(role, p));
  }, [role]);

  /**
   * Check if user has at least the required role level
   */
  const isAtLeast = useCallback((requiredRole) => {
    return hasRoleLevel(role, requiredRole);
  }, [role]);

  /**
   * Check if user is the owner
   */
  const isOwner = useMemo(() => role === 'owner', [role]);

  /**
   * Check if user is admin or higher
   */
  const isAdmin = useMemo(() => hasRoleLevel(role, 'admin'), [role]);

  /**
   * Check if user is manager or higher
   */
  const isManager = useMemo(() => hasRoleLevel(role, 'manager'), [role]);

  /**
   * Get roles that the current user can assign to others
   */
  const assignableRoles = useMemo(() => getAssignableRoles(role), [role]);

  /**
   * Check if user can manage another user based on their role
   */
  const canManageUser = useCallback((targetRole) => {
    if (!role || !targetRole) return false;
    // Owner can manage anyone
    if (role === 'owner') return true;
    // Admin can manage managers and employees
    if (role === 'admin' && ['manager', 'employee'].includes(targetRole)) return true;
    // No one else can manage users
    return false;
  }, [role]);

  return {
    // Permission checks
    can,
    canAll,
    canAny,
    
    // Role checks
    role,
    isOwner,
    isAdmin,
    isManager,
    isAtLeast,
    
    // User management
    assignableRoles,
    canManageUser,
    
    // Utilities
    getRoleName,
    getRoleColor,
    
    // All permissions constant for reference
    PERMISSIONS,
  };
}

export default usePermissions;

